import { z } from 'zod';

import {
  AcceptedEdgeSchema,
  EdgeContextSchema,
  RelationIdSchema,
  type EdgeRejectionReason,
} from './edge.js';
import {
  IsoDateSchema,
  LensIdSchema,
  NonEmptyStringSchema,
  QidSchema,
  Sha256Schema,
} from './primitives.js';
import { SourceSchema } from './source.js';
import { QualifiedTimeSchema } from './time.js';

export const CountByKeySchema = z.record(z.string(), z.number().int().nonnegative());

export const CandidateExampleSchema = z.object({
  edgeId: NonEmptyStringSchema,
  lens: LensIdSchema,
  sourceId: QidSchema,
  sourceName: NonEmptyStringSchema,
  targetId: QidSchema,
  targetName: NonEmptyStringSchema,
  relation: RelationIdSchema,
  directed: z.boolean(),
  start: QualifiedTimeSchema.optional(),
  end: QualifiedTimeSchema.optional(),
  context: EdgeContextSchema.optional(),
  statementIds: z.array(NonEmptyStringSchema),
  decision: z.enum(['accepted', 'rejected']),
  reasons: z.array(NonEmptyStringSchema),
  potentiallyLiving: z.boolean(),
  qualifyingSourceIds: z.array(NonEmptyStringSchema),
  sources: z.array(SourceSchema),
  hasNonQualifyingSources: z.boolean(),
  evidenceRequirementSatisfied: z.boolean(),
});

export type CandidateExample = z.infer<typeof CandidateExampleSchema>;

export const AutomatedPublicationValidationSchema = z.object({
  status: z.enum(['pass', 'fail', 'deferred']),
  checkedAcceptedCount: z.number().int().nonnegative(),
  violations: z.array(NonEmptyStringSchema),
  notes: z.array(NonEmptyStringSchema),
});

export type AutomatedPublicationValidation = z.infer<typeof AutomatedPublicationValidationSchema>;

export const DegreeBucketSchema = z.object({
  minimum: z.number().int().nonnegative(),
  maximum: z.number().int().nonnegative().nullable(),
  people: z.number().int().nonnegative(),
});

export const GraphAuditSchema = z.object({
  people: z.number().int().nonnegative(),
  acceptedEdges: z.number().int().nonnegative(),
  connectedComponents: z.number().int().nonnegative(),
  largestComponentPeople: z.number().int().nonnegative(),
  isolatedPeople: z.number().int().nonnegative(),
  maximumDegree: z.number().int().nonnegative(),
  averageDegree: z.number().finite().nonnegative(),
  density: z.number().finite().nonnegative(),
  degreeBuckets: z.array(DegreeBucketSchema),
});

export type GraphAudit = z.infer<typeof GraphAuditSchema>;

export const LensStructuralMetricsSchema = z.object({
  directedAcceptedCount: z.number().int().nonnegative(),
  datedAcceptedCount: z.number().int().nonnegative(),
  uniqueRelationshipPairs: z.number().int().nonnegative(),
  repeatedPairRecords: z.number().int().nonnegative(),
  graph: GraphAuditSchema,
});

export const LensAuditSchema = z.object({
  lens: LensIdSchema,
  candidateCount: z.number().int().nonnegative(),
  acceptedCount: z.number().int().nonnegative(),
  rejectedCount: z.number().int().nonnegative(),
  acceptedPersonCoverage: z.number().int().nonnegative(),
  potentiallyLivingCandidateCount: z.number().int().nonnegative(),
  potentiallyLivingEvidenceQualifiedCount: z.number().int().nonnegative(),
  potentiallyLivingAcceptedCount: z.number().int().nonnegative(),
  livingEvidenceCoverage: z.number().min(0).max(1).nullable(),
  rejectionReasons: CountByKeySchema,
  relationCounts: CountByKeySchema,
  statusCounts: CountByKeySchema,
  retainedSourceClasses: CountByKeySchema,
  unclassifiedSourceHosts: CountByKeySchema,
  nonQualifyingReferenceQids: CountByKeySchema,
  estimatedJsonBytes: z.number().int().nonnegative(),
  estimatedGzipBytes: z.number().int().nonnegative(),
  structuralMetrics: LensStructuralMetricsSchema.optional(),
  automatedPublicationValidation: AutomatedPublicationValidationSchema,
  recommendation: z.enum(['required-core', 'accept', 'narrow', 'defer']),
  recommendationReasons: z.array(NonEmptyStringSchema),
  acceptedExamples: z.array(CandidateExampleSchema),
  rejectedExamples: z.array(CandidateExampleSchema),
  automatedInspectionSample: z.array(CandidateExampleSchema),
});

export type LensAudit = z.infer<typeof LensAuditSchema>;

export const ConnectivitySelectionAuditSchema = z.object({
  strategyVersion: z.number().int().nonnegative(),
  reverseNeighborCandidates: z.number().int().nonnegative(),
  reverseNeighborEntities: z.number().int().nonnegative(),
  baselinePeople: z.number().int().nonnegative(),
  addedConnectorPeople: z.number().int().nonnegative(),
  connectorPersonIds: z.array(QidSchema),
  eligibleCoreEdges: z.number().int().nonnegative(),
  preEnrichmentGraph: GraphAuditSchema,
  baselineGraph: GraphAuditSchema,
  selectedGraph: GraphAuditSchema,
  baselineDomains: CountByKeySchema,
  selectedDomains: CountByKeySchema,
});

export type ConnectivitySelectionAudit = z.infer<typeof ConnectivitySelectionAuditSchema>;

export const ContextSelectionAuditSchema = z.object({
  strategyVersion: z.number().int().nonnegative(),
  candidateEndpointPeople: z.number().int().nonnegative(),
  normalizedCandidatePeople: z.number().int().nonnegative(),
  knownAdultPeople: z.number().int().nonnegative(),
  inferredAdultPeople: z.number().int().nonnegative(),
  adultCandidatePeople: z.number().int().nonnegative(),
  minorCandidatePeople: z.number().int().nonnegative(),
  unknownAgeCandidatePeople: z.number().int().nonnegative(),
  eligibleCandidatePeople: z.number().int().nonnegative(),
  rejectedMinorPeople: z.number().int().nonnegative(),
  rejectedUnknownAgePeople: z.number().int().nonnegative(),
  acceptedContextEdges: z.number().int().nonnegative(),
  addedContextPeople: z.number().int().nonnegative(),
  contextPersonIds: z.array(QidSchema),
});

export type ContextSelectionAudit = z.infer<typeof ContextSelectionAuditSchema>;

export const SelectionAuditSchema = z.object({
  targetCount: z.number().int().positive(),
  selectedCount: z.number().int().nonnegative(),
  eligibleCandidateCount: z.number().int().nonnegative(),
  resolvedArticleCount: z.number().int().nonnegative(),
  unresolvedArticleCount: z.number().int().nonnegative(),
  humanCandidateCount: z.number().int().nonnegative(),
  requiredSeedIds: z.array(QidSchema),
  missingRequiredSeedIds: z.array(QidSchema),
  domains: CountByKeySchema,
  eligibleDomains: CountByKeySchema,
  eras: CountByKeySchema,
  livingStatus: CountByKeySchema,
  inclusionRoles: CountByKeySchema.default({}),
  sourceMonths: z.array(NonEmptyStringSchema),
  connectivityEnrichment: ConnectivitySelectionAuditSchema.optional(),
  contextEnrichment: ContextSelectionAuditSchema.optional(),
});

export type SelectionAudit = z.infer<typeof SelectionAuditSchema>;

export const PathStepSchema = z.object({
  edge: AcceptedEdgeSchema,
  from: QidSchema,
  to: QidSchema,
});

export const AuditPathSchema = z.object({
  source: QidSchema,
  target: QidSchema,
  selectionReason: NonEmptyStringSchema,
  found: z.boolean(),
  totalCost: z.number().finite().nonnegative().nullable(),
  steps: z.array(PathStepSchema),
  auditOnly: z.literal(true),
});

export type AuditPath = z.infer<typeof AuditPathSchema>;

export const InputProvenanceSchema = z.object({
  namespace: NonEmptyStringSchema,
  cacheKey: Sha256Schema,
  requestDigest: Sha256Schema,
  sourcePeriod: NonEmptyStringSchema.optional(),
});

export type InputProvenance = z.infer<typeof InputProvenanceSchema>;

export const FeasibilityAuditSchema = z.object({
  schemaVersion: z.literal(2),
  configVersion: z.number().int().positive(),
  policyVersion: z.number().int().positive(),
  snapshotDate: IsoDateSchema,
  snapshotId: NonEmptyStringSchema,
  configDigest: Sha256Schema,
  inputDigest: Sha256Schema,
  selection: SelectionAuditSchema,
  lenses: z.array(LensAuditSchema),
  graph: GraphAuditSchema,
  paths: z.array(AuditPathSchema),
  inputProvenance: z.array(InputProvenanceSchema),
  passConditions: z.object({
    selectablePeople: z.boolean(),
    requiredSeeds: z.boolean(),
    personalAndFamilyPublishable: z.boolean(),
    cachedRerunByteStable: z.boolean(),
    unattendedPublicationPolicySatisfied: z.boolean(),
  }),
  limitations: z.array(NonEmptyStringSchema),
});

export type FeasibilityAudit = z.infer<typeof FeasibilityAuditSchema>;

export type LensRejectionCounts = Partial<Record<EdgeRejectionReason, number>>;
